/**
 * Universal VoIP device hook — dynamically loads the right SDK
 * based on the active provider from /api/voip/token.
 *
 * Supported providers:
 * - twilio: Uses @twilio/voice-sdk (WebRTC)
 * - signalwire: Twilio-compatible SDK (WebRTC)
 * - plivo: Uses plivo-browser-sdk (WebRTC)
 * - freeswitch: SIP.js (planned)
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { voipApi } from '../api/endpoints'
import { useAuthStore } from '../stores/auth'
import { maskPhone } from '../utils/masking'

export type DeviceStatus = 'offline' | 'ready' | 'connecting' | 'ringing-inbound' | 'on-call' | 'error' | 'no-webrtc'
export type CallOutcome = 'none' | 'cancelled' | 'connected' | 'not_connected' | 'failed'

export interface IncomingCallInfo {
  callerName: string
  callerNumber: string
  extraHeaders: Record<string, string>
}

interface UseVoIPDeviceReturn {
  status: DeviceStatus
  error: string | null
  provider: string | null
  callOutcome: CallOutcome
  connect: (phoneNumber: string) => void
  disconnect: () => void
  toggleMute: () => void
  acceptIncoming: () => void
  rejectIncoming: () => void
  isMuted: boolean
  duration: number
  hasWebRTC: boolean
  incomingCall: IncomingCallInfo | null
  activeCallNumber: string | null  // phone number of current call (inbound or outbound)
  connectionUnstable: boolean  // true when ICE connection is disrupted mid-call
}

const WEBRTC_PROVIDERS = ['twilio', 'signalwire', 'plivo']
const REST_ONLY_PROVIDERS = ['freeswitch']

export function useVoIPDevice(): UseVoIPDeviceReturn {
  const [status, setStatus] = useState<DeviceStatus>('offline')
  const [error, setError] = useState<string | null>(null)
  const [provider, setProvider] = useState<string | null>(null)
  const [isMuted, setIsMuted] = useState(false)
  const [duration, setDuration] = useState(0)
  const [callOutcome, setCallOutcome] = useState<CallOutcome>('none')
  const [incomingCall, setIncomingCall] = useState<IncomingCallInfo | null>(null)
  const [activeCallNumber, setActiveCallNumber] = useState<string | null>(null)
  const [connectionUnstable, setConnectionUnstable] = useState(false)
  const iceRecoveryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reRegisterPollRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastRegisteredAtRef = useRef<number>(0)
  const STALE_SESSION_MS = 30 * 60 * 1000 // 30 minutes
  const statusRef = useRef<DeviceStatus>('offline')
  const wasAnsweredRef = useRef(false)
  const didRingRef = useRef(false)
  const incomingPendingRef = useRef(false)  // true when popup is showing, false after user accepts/rejects
  const incomingAutoAnsweredRef = useRef(false)  // true if SDK auto-answered before user clicked Accept
  const outboundInProgressRef = useRef(false)  // true when user initiated connect(), false when call ends
  const userAcceptedIncomingRef = useRef(false)  // true only when user explicitly clicks Accept
  const incomingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const deviceRef = useRef<any>(null)
  const callRef = useRef<any>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const incomingNotifRef = useRef<Notification | null>(null)

  const hasWebRTC = WEBRTC_PROVIDERS.includes(provider || '')
  // Keep statusRef in sync so event handler closures read current value
  useEffect(() => { statusRef.current = status }, [status])

  useEffect(() => {
    const init = async () => {
      // Skip Plivo SDK initialization inside native mobile WebView —
      // the mobile app handles calls via Linphone, not the browser SDK.
      // Without this guard, the WebView's Plivo SDK receives SIP events
      // for mobile calls and shows the call bar incorrectly.
      try {
        const { isNativeMobile } = await import('../utils/platform')
        if (isNativeMobile()) {
          setStatus('no-webrtc')
          setError(null)
          return
        }
      } catch { /* platform check failed — continue with SDK init */ }

      try {
        const { data } = await voipApi.token()

        const providerName = data.provider
        setProvider(providerName)

        // REST-only providers (no WebRTC) — set no-webrtc status, not error
        if (!data.token || REST_ONLY_PROVIDERS.includes(providerName || '')) {
          if (providerName && REST_ONLY_PROVIDERS.includes(providerName)) {
            setStatus('no-webrtc')
            setError(null)
          } else if (!data.token) {
            setError('Failed to get VoIP token. Check provider settings.')
            setStatus('error')
          }
          return
        }

        if (providerName === 'twilio' || providerName === 'signalwire') {
          await initTwilio(data.token)
        } else if (providerName === 'plivo') {
          await initPlivo(data.token)
        } else {
          setStatus('no-webrtc')
          setError(null)
        }
      } catch (err: any) {
        console.error('VoIP init failed:', err)
        setError(err.message || 'Failed to initialize VoIP')
        setStatus('error')
      }
    }

    init()

    return () => {
      if (deviceRef.current) {
        // Clear keepalive and reconnect timers
        if ((deviceRef.current as any)._keepaliveInterval) {
          clearInterval((deviceRef.current as any)._keepaliveInterval)
        }
        if ((deviceRef.current as any)._cleanupReconnect) {
          (deviceRef.current as any)._cleanupReconnect()
        }
        // Cleanup based on provider type
        if (deviceRef.current.destroy) deviceRef.current.destroy()
        if (deviceRef.current.logout) deviceRef.current.logout()
        deviceRef.current = null
      }
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [])

  // ── Twilio / SignalWire initialization ──
  async function initTwilio(token: string) {
    try {
      const { Device, Call } = await import('@twilio/voice-sdk')
      const device = new Device(token, {
        logLevel: 1,
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
      })

      device.on('registered', () => { setStatus('ready'); setError(null) })
      device.on('error', (err: any) => { setError(err.message || 'Device error'); setStatus('error') })
      device.on('incoming', (call: any) => { call.accept(); setupTwilioCallHandlers(call) })

      await device.register()
      deviceRef.current = device
    } catch (sdkError: any) {
      console.error('Twilio SDK failed:', sdkError)
      setError('Browser calling SDK failed to load.')
      setStatus('no-webrtc')
    }
  }

  function computeOutcome(): CallOutcome {
    if (wasAnsweredRef.current) return 'connected'
    if (didRingRef.current) return 'not_connected'
    return 'failed'
  }

  function setupTwilioCallHandlers(call: any) {
    callRef.current = call
    call.on('accept', () => { setStatus('on-call'); wasAnsweredRef.current = true; startTimer() })
    call.on('ringing', () => { setStatus('connecting'); didRingRef.current = true })
    call.on('disconnect', () => { setCallOutcome(computeOutcome()); setStatus('ready'); cleanupCall() })
    call.on('cancel', () => { setCallOutcome('cancelled'); setStatus('ready'); cleanupCall() })
    call.on('error', (err: any) => { setError(err.message); setCallOutcome('failed'); setStatus('ready'); cleanupCall() })
  }

  // ── Plivo initialization ──
  async function initPlivo(tokenJson: string) {
    try {
      // Token is a JSON string with {username, password}
      let credentials: { username: string; password: string }
      try {
        credentials = JSON.parse(tokenJson)
      } catch {
        setError('Plivo endpoint credentials not configured. Go to Settings → VoIP → Plivo and set Endpoint Username/Password.')
        setStatus('error')
        return
      }

      if (!credentials.username || !credentials.password) {
        setError('Plivo endpoint credentials not configured. Create an endpoint in Plivo Console → Voice → Endpoints, then add credentials in Settings.')
        setStatus('error')
        return
      }

      const PlivoBrowserSdk = await import('plivo-browser-sdk')
      const Plivo = (PlivoBrowserSdk as any).default || (PlivoBrowserSdk as any).Plivo || PlivoBrowserSdk

      const plivoClient = new Plivo({
        debug: 'INFO',
        permOnClick: true,
        enableTracking: true,
        closeProtection: false,
        allowMultipleIncomingCalls: false,
      })

      let hasEverLoggedIn = false

      plivoClient.client.on('onLogin', () => {
        console.log('[VoIP] Plivo login success')
        hasEverLoggedIn = true
        lastRegisteredAtRef.current = Date.now()
        setStatus('ready')
        setError(null)
      })

      plivoClient.client.on('onLoginFailed', (cause: any) => {
        console.error('[VoIP] Plivo login failed:', cause)
        if (!hasEverLoggedIn) {
          // First-time login failure — bad credentials, show permanent error
          setError(`Plivo login failed: ${cause || 'check endpoint credentials'}`)
          setStatus('error')
        }
        // If hasEverLoggedIn, the reconnect handler (set up below) will retry
      })

      plivoClient.client.on('onCallRemoteRinging', () => {
        if (!outboundInProgressRef.current) {
          // Not our call — another device using the same endpoint
          console.log('[VoIP] Ignoring onCallRemoteRinging — no outbound call in progress')
          return
        }
        setStatus('connecting')
        didRingRef.current = true
      })

      // ICE connection state monitoring — detect silent network drops mid-call
      function setupIceMonitoring(client: any) {
        try {
          const pc: RTCPeerConnection | null =
            client?.client?.callSession?.peerConnection ??
            client?.client?.callSession?.mediaSession?.peerConnection ??
            client?.callSession?.peerConnection ??
            null
          if (!pc) {
            console.log('[VoIP] RTCPeerConnection not accessible — ICE monitoring unavailable')
            return
          }
          console.log('[VoIP] ICE monitoring active')
          pc.oniceconnectionstatechange = () => {
            const state = pc.iceConnectionState
            console.log('[VoIP] ICE state:', state)
            if (state === 'disconnected') {
              setConnectionUnstable(true)
              // Start 10s recovery window
              if (iceRecoveryTimerRef.current) clearTimeout(iceRecoveryTimerRef.current)
              iceRecoveryTimerRef.current = setTimeout(() => {
                // Still disconnected after 10s — end the call cleanly
                console.log('[VoIP] ICE recovery timeout — terminating call')
                setError('Call dropped — connection lost')
                try { if (client.client?.hangup) client.client.hangup(); else client.hangup?.() } catch { /* ignore */ }
                setConnectionUnstable(false)
              }, 10_000)
            } else if (state === 'connected' || state === 'completed') {
              // Recovered
              if (iceRecoveryTimerRef.current) {
                clearTimeout(iceRecoveryTimerRef.current)
                iceRecoveryTimerRef.current = null
              }
              setConnectionUnstable(false)
            } else if (state === 'failed' || state === 'closed') {
              // Unrecoverable — end call immediately
              if (iceRecoveryTimerRef.current) {
                clearTimeout(iceRecoveryTimerRef.current)
                iceRecoveryTimerRef.current = null
              }
              console.log('[VoIP] ICE failed/closed — terminating call')
              setError('Call dropped — connection lost')
              try { if (client.client?.hangup) client.client.hangup(); else client.hangup?.() } catch { /* ignore */ }
              setConnectionUnstable(false)
            }
          }
        } catch (e) {
          console.log('[VoIP] ICE monitoring setup failed:', e)
        }
      }

      plivoClient.client.on('onCallAnswered', () => {
        console.log('[VoIP] onCallAnswered, incomingPending:', incomingPendingRef.current, 'outbound:', outboundInProgressRef.current)
        if (!incomingPendingRef.current && !outboundInProgressRef.current) {
          // No outbound call in progress and no incoming call pending on THIS device.
          // This is a call happening on another device (mobile app) using the same
          // Plivo endpoint. Ignore it — don't show call UI on desktop.
          console.log('[VoIP] Ignoring onCallAnswered — call is on another device (mobile?)')
          return
        }
        wasAnsweredRef.current = true
        if (incomingPendingRef.current) {
          // SDK auto-answered (Plivo Dial<User> connects immediately).
          // Don't transition to on-call yet — wait for user to click Accept.
          // But start the timer now so duration reflects actual call time.
          incomingAutoAnsweredRef.current = true
          startTimer()
          setupIceMonitoring(plivoClient)
          return
        }
        // Outbound call answered by the other party, or user already accepted
        setStatus('on-call')
        startTimer()
        setupIceMonitoring(plivoClient)
      })

      plivoClient.client.on('onCallTerminated', () => {
        console.log('[VoIP] Call terminated, outbound:', outboundInProgressRef.current, 'incomingPending:', incomingPendingRef.current, 'autoAnswered:', incomingAutoAnsweredRef.current, 'userAccepted:', userAcceptedIncomingRef.current)
        // Always clear incoming timeout
        if (incomingTimeoutRef.current) { clearTimeout(incomingTimeoutRef.current); incomingTimeoutRef.current = null }

        const wasOurCall = outboundInProgressRef.current || incomingPendingRef.current || incomingAutoAnsweredRef.current || wasAnsweredRef.current || userAcceptedIncomingRef.current
        if (!wasOurCall) {
          console.log('[VoIP] Ignoring onCallTerminated — call was on another device')
          return
        }
        setCallOutcome(computeOutcome())
        setIncomingCall(null)
        setActiveCallNumber(null)
        incomingPendingRef.current = false
        incomingAutoAnsweredRef.current = false
        userAcceptedIncomingRef.current = false
        setStatus('ready')
        setIsMuted(false)
        cleanupCall()
      })

      plivoClient.client.on('onCallFailed', (cause: any) => {
        console.error('[VoIP] Call failed:', cause)
        setError(`Call failed: ${cause || 'unknown error'}`)
        setCallOutcome(didRingRef.current ? 'not_connected' : 'failed')
        setIncomingCall(null)
        setActiveCallNumber(null)
        incomingPendingRef.current = false
        incomingAutoAnsweredRef.current = false
        setStatus('ready')
        setIsMuted(false)
        cleanupCall()
      })

      plivoClient.client.on('onIncomingCall', (callerId: string, extraHeaders: Record<string, string>) => {
        console.log('[VoIP] Incoming call received, callerId:', callerId, 'extraHeaders:', extraHeaders)

        // Concurrent inbound while on a call — reject and create notification
        if (statusRef.current === 'on-call') {
          console.log('[VoIP] Already on call — rejecting concurrent inbound from', callerId)
          try { plivoClient.client.reject() } catch { /* may already be auto-rejected */ }
          // Fire-and-forget notification to backend
          fetch('/api/voip/missed-while-busy', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              caller_phone: callerId?.split('@')[0]?.replace(/^sip:/, '') || null,
            }),
          }).catch(() => {})
          return
        }

        // Show incoming call UI — don't auto-answer

        // Extract the real caller number from the SIP URI
        // Plivo sends callerId as "number@phone.plivo.com" or "+91XXXXXXXXXX@phone.plivo.com"
        let callerNumber = callerId || 'Unknown'
        if (callerNumber.includes('@')) {
          callerNumber = callerNumber.split('@')[0]  // strip @phone.plivo.com
        }
        if (callerNumber.startsWith('sip:')) {
          callerNumber = callerNumber.slice(4)  // strip sip: prefix
        }
        // Format: add + prefix if it looks like an international number without one
        if (/^\d{10,}$/.test(callerNumber)) {
          callerNumber = `+${callerNumber}`
        }

        // Also check extra headers for caller ID (X-PH- prefix required by Plivo)
        const headerCaller = extraHeaders?.['X-PH-Callerid'] || extraHeaders?.['X-Ph-Callerid'] || extraHeaders?.['X-PH-callerid'] || ''
        if (headerCaller && !headerCaller.includes('@')) {
          callerNumber = headerCaller
        }

        // Mask caller number if contact masking is enabled for this user
        const shouldMask = useAuthStore.getState().maskContactInfo
        const displayNumber = shouldMask ? maskPhone(callerNumber) : callerNumber

        // Show notification and popup immediately with the phone number
        const showNotification = (name: string | null, number: string) => {
          try {
            if ('Notification' in window && Notification.permission === 'granted') {
              incomingNotifRef.current?.close()
              const n = new Notification('Incoming Call', {
                body: name ? `${name}\n${number}` : number,
                icon: '/favicon.svg',
                tag: 'incoming-call',
                requireInteraction: true,
              })
              n.onclick = () => { window.focus(); n.close() }
              setTimeout(() => n.close(), 30000)
              incomingNotifRef.current = n
            }
          } catch { /* notification not supported */ }
        }

        // Show immediately with number only
        showNotification(null, displayNumber)

        setIncomingCall({
          callerName: displayNumber,
          callerNumber: displayNumber,
          extraHeaders: extraHeaders || {},
        })

        // Then do API lookup for lead name — updates popup and notification
        voipApi.callerInfo(callerNumber).then((resp) => {
          const leadName = resp?.lead_name || null
          const apiNumber = resp?.display_number || displayNumber
          if (leadName) {
            showNotification(leadName, apiNumber)
            setIncomingCall({
              callerName: leadName,
              callerNumber: apiNumber,
              extraHeaders: extraHeaders || {},
            })
            setActiveCallNumber(apiNumber)
          }
        }).catch(() => { /* non-critical — popup already shows number */ })
        incomingPendingRef.current = true
        userAcceptedIncomingRef.current = false
        // Only reset auto-answered if onCallAnswered hasn't already fired (race condition)
        if (!wasAnsweredRef.current) {
          incomingAutoAnsweredRef.current = false
        }
        setActiveCallNumber(displayNumber)
        setStatus('ringing-inbound')
        setCallOutcome('none')
        wasAnsweredRef.current = false
        didRingRef.current = true
        setIsMuted(false)
        setDuration(0)

        // Safety timeout: if no one answers on THIS device within 45s, reset.
        // This prevents the call bar from sticking when another device (mobile)
        // handles the call and the web SDK doesn't receive onCallTerminated.
        if (incomingTimeoutRef.current) clearTimeout(incomingTimeoutRef.current)
        incomingTimeoutRef.current = setTimeout(() => {
          if (incomingPendingRef.current && !userAcceptedIncomingRef.current) {
            console.log('[VoIP] Incoming call timeout — resetting (likely handled by another device)')
            setIncomingCall(null)
            setActiveCallNumber(null)
            incomingPendingRef.current = false
            incomingAutoAnsweredRef.current = false
            setStatus('ready')
            cleanupCall()
          }
        }, 45_000)
      })

      plivoClient.client.on('onIncomingCallCanceled', () => {
        console.log('[VoIP] Incoming call canceled by caller')
        if (incomingTimeoutRef.current) { clearTimeout(incomingTimeoutRef.current); incomingTimeoutRef.current = null }
        setIncomingCall(null)
        incomingPendingRef.current = false
        incomingAutoAnsweredRef.current = false
        userAcceptedIncomingRef.current = false
        setCallOutcome('cancelled')
        setStatus('ready')
        cleanupCall()
      })

      plivoClient.client.on('onMediaPermission', (permGranted: boolean) => {
        if (!permGranted) {
          setError('Microphone permission denied. Allow microphone access to make calls.')
        }
      })

      // Reconnect state — shared between onConnectionChange, onLoginFailed, and keepalive
      let reconnectAttempt = 0
      let reconnectTimer: ReturnType<typeof setTimeout> | null = null
      let isReconnecting = false
      const MAX_RECONNECT_DELAY = 60000 // 60s max
      const MAX_RECONNECT_ATTEMPTS = 10

      const scheduleReconnect = (reason: string) => {
        // Don't stack multiple reconnect timers
        if (reconnectTimer) return
        if (reconnectAttempt >= MAX_RECONNECT_ATTEMPTS) {
          console.log(`[VoIP] Max reconnect attempts (${MAX_RECONNECT_ATTEMPTS}) reached, giving up`)
          setError('Connection lost. Refresh the page to reconnect.')
          setStatus('error')
          return
        }
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempt), MAX_RECONNECT_DELAY)
        reconnectAttempt++
        isReconnecting = true
        console.log(`[VoIP] ${reason}, reconnecting in ${delay}ms (attempt ${reconnectAttempt})...`)
        setStatus('connecting')
        reconnectTimer = setTimeout(() => {
          reconnectTimer = null
          try {
            plivoClient.client.login(credentials.username, credentials.password)
          } catch (e) {
            console.error('[VoIP] Reconnect login() threw:', e)
            scheduleReconnect('login() threw')
          }
        }, delay)
      }

      // Handle login failures during reconnect — retry with backoff
      plivoClient.client.on('onLoginFailed', (cause: any) => {
        if (isReconnecting && hasEverLoggedIn) {
          console.log('[VoIP] Reconnect login failed:', cause)
          scheduleReconnect('Login failed during reconnect')
        }
      })

      plivoClient.client.on('onConnectionChange', (event: any) => {
        console.log('[VoIP] Connection change:', JSON.stringify(event))
        if (event?.state === 'connected') {
          reconnectAttempt = 0
          isReconnecting = false
          if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null }
        }
        if (event?.state === 'disconnected' || event?.state === 'failed') {
          scheduleReconnect('WebSocket ' + (event?.state || 'disconnected'))
        }
      })

      // Periodic keepalive — only checks, does NOT trigger reconnect if one is pending
      const keepaliveInterval = setInterval(() => {
        if (isReconnecting || reconnectTimer) return // Don't interfere with backoff
        try {
          if (plivoClient.client.isLoggedIn && !plivoClient.client.isLoggedIn()) {
            console.log('[VoIP] Keepalive: not logged in, triggering reconnect')
            scheduleReconnect('Keepalive detected offline')
          }
        } catch {
          // isLoggedIn may not exist on all SDK versions
        }
      }, 30000)

      // Login with endpoint credentials
      plivoClient.client.login(credentials.username, credentials.password)
      deviceRef.current = plivoClient
      // Store credentials for proactive re-register
      ;(plivoClient as any)._plivoCredentials = credentials
      // Store refs for cleanup
      ;(plivoClient as any)._keepaliveInterval = keepaliveInterval
      ;(plivoClient as any)._reconnectTimer = reconnectTimer
      ;(plivoClient as any)._cleanupReconnect = () => {
        if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null }
        isReconnecting = false
      }
    } catch (sdkError: any) {
      console.error('Plivo SDK failed:', sdkError)
      setError('Plivo Browser SDK failed to load: ' + (sdkError.message || ''))
      setStatus('no-webrtc')
    }
  }

  // ── Shared helpers ──
  function startTimer() {
    setDuration(0)
    timerRef.current = setInterval(() => setDuration(d => d + 1), 1000)
  }

  function cleanupCall() {
    callRef.current = null
    outboundInProgressRef.current = false
    incomingNotifRef.current?.close()
    incomingNotifRef.current = null
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    if (iceRecoveryTimerRef.current) {
      clearTimeout(iceRecoveryTimerRef.current)
      iceRecoveryTimerRef.current = null
    }
    if (reRegisterPollRef.current) {
      clearTimeout(reRegisterPollRef.current)
      reRegisterPollRef.current = null
    }
    setConnectionUnstable(false)
  }

  // ── Connect ──
  const connect = useCallback((phoneNumber: string) => {
    setStatus('connecting')
    setError(null)
    setIsMuted(false)
    setCallOutcome('none')
    setActiveCallNumber(phoneNumber)
    wasAnsweredRef.current = false
    didRingRef.current = false
    outboundInProgressRef.current = true

    if (provider === 'twilio' || provider === 'signalwire') {
      if (!deviceRef.current) { setError('Device not ready'); setStatus('ready'); return }
      deviceRef.current.connect({ params: { To: phoneNumber } }).then((call: any) => {
        setupTwilioCallHandlers(call)
      }).catch((err: any) => {
        setError(err.message || 'Failed to connect')
        setStatus('ready')
      })
    } else if (provider === 'plivo') {
      if (!deviceRef.current?.client) { setError('Plivo not ready'); setStatus('ready'); return }
      // Proactive re-register if session is stale (>30 min idle)
      const sessionAge = Date.now() - lastRegisteredAtRef.current
      if (lastRegisteredAtRef.current > 0 && sessionAge > STALE_SESSION_MS) {
        console.log(`[VoIP] Session stale (${Math.round(sessionAge / 60000)}m), re-registering before call`)
        try {
          deviceRef.current.client.logout()
          deviceRef.current.client.login(
            deviceRef.current._plivoCredentials?.username,
            deviceRef.current._plivoCredentials?.password,
          )
          // Poll for re-registration, with cleanup on unmount via ref
          let attempts = 0
          const maxAttempts = 50 // 50 * 200ms = 10s
          const pollReady = () => {
            reRegisterPollRef.current = setTimeout(() => {
              attempts++
              if (lastRegisteredAtRef.current > Date.now() - 5000) {
                reRegisterPollRef.current = null
                deviceRef.current?.client?.call(phoneNumber)
              } else if (attempts >= maxAttempts) {
                reRegisterPollRef.current = null
                console.log('[VoIP] Re-register timeout — aborting call')
                setError('Unable to reconnect. Please try again.')
                setStatus('ready')
                outboundInProgressRef.current = false
              } else {
                pollReady()
              }
            }, 200)
          }
          pollReady()
          return
        } catch (e) {
          console.error('[VoIP] Proactive re-register failed:', e)
          // Fall through to attempt the call anyway
        }
      }
      // Plivo call — pass the phone number directly
      deviceRef.current.client.call(phoneNumber)
    } else {
      // REST API fallback
      setDuration(0)
      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000)
      setTimeout(() => setStatus('on-call'), 1000)
    }
  }, [provider])

  // ── Disconnect ──
  const disconnect = useCallback(() => {
    setCallOutcome(computeOutcome())

    if (provider === 'twilio' || provider === 'signalwire') {
      if (callRef.current) callRef.current.disconnect()
    } else if (provider === 'plivo') {
      if (deviceRef.current?.client) deviceRef.current.client.hangup()
    }
    callRef.current = null
    setIncomingCall(null)
    setActiveCallNumber(null)
    incomingPendingRef.current = false
    incomingAutoAnsweredRef.current = false
    outboundInProgressRef.current = false
    setIsMuted(false)
    setConnectionUnstable(false)
    setStatus(hasWebRTC ? 'ready' : 'no-webrtc')
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
    if (iceRecoveryTimerRef.current) { clearTimeout(iceRecoveryTimerRef.current); iceRecoveryTimerRef.current = null }
  }, [provider, hasWebRTC])

  // ── Mute ──
  const toggleMute = useCallback(() => {
    if (provider === 'twilio' || provider === 'signalwire') {
      if (callRef.current) {
        const newMuted = !callRef.current.isMuted()
        callRef.current.mute(newMuted)
        setIsMuted(newMuted)
      }
    } else if (provider === 'plivo') {
      if (deviceRef.current?.client) {
        if (isMuted) {
          deviceRef.current.client.unmute()
        } else {
          deviceRef.current.client.mute()
        }
        setIsMuted(!isMuted)
      }
    }
  }, [provider, isMuted])

  // ── Accept incoming call ──
  const acceptIncoming = useCallback(() => {
    console.log('[VoIP] Accepting incoming call, autoAnswered:', incomingAutoAnsweredRef.current)
    userAcceptedIncomingRef.current = true
    incomingPendingRef.current = false
    if (incomingTimeoutRef.current) { clearTimeout(incomingTimeoutRef.current); incomingTimeoutRef.current = null }

    if (incomingAutoAnsweredRef.current) {
      // SDK already connected the audio and timer is already running — just update UI
      incomingAutoAnsweredRef.current = false
      setIncomingCall(null)
      setStatus('on-call')
      wasAnsweredRef.current = true
      // Timer already started in onCallAnswered — don't restart
      return
    }

    // SDK hasn't auto-answered — tell it to answer now
    if (provider === 'twilio' || provider === 'signalwire') {
      if (callRef.current) callRef.current.accept()
    } else if (provider === 'plivo') {
      if (deviceRef.current?.client) deviceRef.current.client.answer()
    }
    setIncomingCall(null)
    setStatus('connecting')
    didRingRef.current = true
  }, [provider])

  // ── Reject incoming call ──
  const rejectIncoming = useCallback(() => {
    console.log('[VoIP] Rejecting incoming call')
    incomingPendingRef.current = false
    incomingAutoAnsweredRef.current = false
    userAcceptedIncomingRef.current = false
    if (incomingTimeoutRef.current) { clearTimeout(incomingTimeoutRef.current); incomingTimeoutRef.current = null }
    if (provider === 'twilio' || provider === 'signalwire') {
      if (callRef.current) callRef.current.reject()
    } else if (provider === 'plivo') {
      // If already auto-answered, hangup instead of reject
      if (deviceRef.current?.client) {
        try { deviceRef.current.client.hangup() } catch { /* ignore */ }
        try { deviceRef.current.client.reject() } catch { /* ignore */ }
      }
    }
    setIncomingCall(null)
    setCallOutcome('cancelled')
    setStatus('ready')
  }, [provider])

  return { status, error, provider, callOutcome, connect, disconnect, toggleMute, acceptIncoming, rejectIncoming, isMuted, duration, hasWebRTC, incomingCall, activeCallNumber, connectionUnstable }
}
